import { matchPath } from 'react-router';
import { routePaths } from 'src/utils/constants';
import { AnalyticsEvents } from './events';
import { getDistinctId, initAnalytics, track } from './mixpanel';

/**
 * The hop to WorkOS AuthKit and back (NPT-1694).
 *
 * Every sign-in / sign-up button leaves the site through AuthContext.signIn/signUp, and nothing
 * was emitted between that click and the server's `user_registered`, so the step every converting
 * traveler passes through was a blind spot. `auth_handoff_started` fires just before the redirect
 * and leaves a record in sessionStorage; `auth_handoff_returned` fires on the first page load after
 * it, with `success` / `abandoned` and the time spent away. A started event with no returned one
 * means the traveler never came back.
 */

/** Which button sent the traveler to WorkOS. `unknown` only for a call site nobody tagged. */
export type AuthHandoffTrigger =
   | 'rate_paywall'
   | 'map_rate_paywall'
   | 'price_track'
   | 'favorite'
   | 'header'
   | 'search_paywall'
   | 'search_inlist_card'
   | 'landing_cta'
   | 'season_planner'
   | 'register_page'
   | 'signup_page'
   | 'protected_route'
   | 'login_page'
   | 'unknown';

export interface AuthHandoffContext {
   trigger: AuthHandoffTrigger;
   hotelId?: string;
   hotelName?: string;
   roomName?: string;
   checkin?: string;
   checkout?: string;
}

export type AuthKind = 'sign_in' | 'sign_up';

interface HandoffRecord {
   id: string;
   pageLoadId: string;
   startedAt: number;
   authKind: AuthKind;
   provider: string;
   trigger: AuthHandoffTrigger;
   surface: string;
   anonymousId: string | null;
   hotelId?: string;
}

export const AUTH_HANDOFF_STORAGE_KEY = 'npt_auth_handoff';

/** A second click on the same page inside this window is the same handoff, not a new attempt. */
const REPEAT_CLICK_WINDOW_MS = 10_000;

const randomId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * Identifies this document load. A record carrying it was started on this very page, so the
 * traveler has not been to WorkOS and back yet.
 */
const PAGE_LOAD_ID = randomId();

/** Most specific first — matchPath would otherwise read `hotels/map-view` as a hotel id. */
const SURFACES: [surface: string, pattern: string][] = [
   ['hotel_detail_map_view', routePaths.HOTEL_DETAIL_MAP_VIEW],
   ['hotel_gallery', routePaths.HOTEL_GALLERY],
   ['hotel_map_view', routePaths.HOTEL_MAP_VIEW],
   ['hotel_detail', routePaths.HOTEL_DETAIL],
   ['hotel_search', routePaths.HOTEL_SEARCH],
   ['booking', routePaths.BOOKING],
   ['event_landing', routePaths.EVENT.LANDING],
   ['game_guide', routePaths.GAME_GUIDE],
   ['plan_season', routePaths.PLAN_SEASON_PROGRAM],
   ['plan_season', routePaths.PLAN_SEASON],
   ['organization_details', routePaths.ORGANIZATION.DETAILS],
   ['organization_landing', routePaths.ORGANIZATION.LANDING],
   ['landing', routePaths.LANDING],
   ['login_page', routePaths.LOGIN_PAGE],
   ['register_page', routePaths.REGISTER_PAGE],
   ['signup_page', routePaths.SIGNUP],
   ['my_favorites', routePaths.MY_FAVORITES],
   ['my_price_tracking', routePaths.MY_PRICE_TRACKING],
   ['my_trips', routePaths.MY_TRIPS],
];

export const resolveSurface = (pathname: string): string =>
   SURFACES.find(([, pattern]) => matchPath(pattern, pathname))?.[0] ?? 'other';

export const clearAuthHandoff = (): void => {
   try {
      sessionStorage.removeItem(AUTH_HANDOFF_STORAGE_KEY);
   } catch { /* private mode */ }
};

const isHandoffRecord = (value: unknown): value is HandoffRecord => {
   const record = value as Partial<HandoffRecord> | null;
   return !!record && typeof record.id === 'string' && typeof record.pageLoadId === 'string' && typeof record.startedAt === 'number';
};

/** A record that can't be read back is dropped rather than reported. */
const readRecord = (): HandoffRecord | null => {
   try {
      const raw = sessionStorage.getItem(AUTH_HANDOFF_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (isHandoffRecord(parsed)) return parsed;
   } catch { /* private mode or corrupt JSON */ }
   clearAuthHandoff();
   return null;
};

const trackReturned = (record: HandoffRecord, outcome: 'success' | 'abandoned') => {
   track(AnalyticsEvents.AUTH_HANDOFF_RETURNED, {
      outcome,
      elapsed_ms: Math.max(0, Date.now() - record.startedAt),
      handoff_id: record.id,
      auth_kind: record.authKind,
      provider: record.provider,
      trigger: record.trigger,
      surface: record.surface,
      anonymous_id: record.anonymousId,
      hotelId: record.hotelId,
   });
};

/** Call just before redirecting to WorkOS. Never throws. */
export const startAuthHandoff = (authKind: AuthKind, provider: string | undefined, handoff?: AuthHandoffContext): void => {
   // AuthProvider's effects run before MixpanelProvider's init effect, so the SDK may not be up yet.
   initAnalytics();
   const now = Date.now();
   const existing = readRecord();
   if (existing) {
      if (existing.pageLoadId === PAGE_LOAD_ID && now - existing.startedAt < REPEAT_CLICK_WINDOW_MS) return;
      // Still open from an earlier page load: the traveler came back and is being sent to sign in
      // again, so they did not sign in last time. Child effects such as ProtectedRoute's redirect
      // run before AuthProvider gets to close it.
      if (existing.pageLoadId !== PAGE_LOAD_ID) trackReturned(existing, 'abandoned');
   }

   const record: HandoffRecord = {
      id: randomId(),
      pageLoadId: PAGE_LOAD_ID,
      startedAt: now,
      authKind,
      provider: provider ?? 'authkit',
      trigger: handoff?.trigger ?? 'unknown',
      surface: resolveSurface(window.location.pathname),
      anonymousId: getDistinctId(),
      hotelId: handoff?.hotelId,
   };
   try {
      sessionStorage.setItem(AUTH_HANDOFF_STORAGE_KEY, JSON.stringify(record));
   } catch { /* private mode — the started event still goes out, there just won't be a returned one */ }

   track(AnalyticsEvents.AUTH_HANDOFF_STARTED, {
      handoff_id: record.id,
      auth_kind: record.authKind,
      provider: record.provider,
      trigger: record.trigger,
      surface: record.surface,
      anonymous_id: record.anonymousId,
      hotelId: handoff?.hotelId,
      hotelName: handoff?.hotelName,
      roomName: handoff?.roomName,
      checkin: handoff?.checkin,
      checkout: handoff?.checkout,
   });
};

/**
 * Call once the auth state after a possible return is known. No-op without an open handoff, or
 * when the handoff was started on this same page load — unless the page was restored from the
 * back-forward cache, which keeps the old page-load id.
 */
export const completeAuthHandoff = ({ authenticated, restoredFromCache = false }: { authenticated: boolean; restoredFromCache?: boolean }): void => {
   initAnalytics();
   const record = readRecord();
   if (!record) return;
   if (record.pageLoadId === PAGE_LOAD_ID && !restoredFromCache) return;
   clearAuthHandoff();
   trackReturned(record, authenticated ? 'success' : 'abandoned');
};
